import { useCallback, useEffect, useRef, useState } from 'react';

interface Resultado<T> {
  /** Intento al que corresponden estos datos; sirve para saber si están al día. */
  intento: number;
  datos: T;
  error: unknown;
}

export interface EstadoAsincrono<T> {
  datos: T;
  cargando: boolean;
  error: unknown;
  /** Vuelve a pedir los datos. Útil tras crear, editar o borrar. */
  recargar: () => void;
}

/**
 * Carga datos de un servicio asíncrono y expone `cargando` / `error`.
 *
 * Se usa en todas las páginas que consumen noticias y slides, para que dé igual
 * si los datos salen del almacén en memoria o de una llamada HTTP.
 *
 * `cargar` tiene que ser estable (envuélvelo en `useCallback`): es lo que marca
 * cuándo hay que volver a pedir los datos. Recibe un `AbortSignal` que se
 * dispara si el componente se desmonta o si se lanza una carga nueva, de modo
 * que una respuesta que llega tarde no pisa a la buena.
 *
 * `cargando` es estado derivado, no una bandera que se ponga a mano dentro del
 * efecto: así no hay un `setState` síncrono en el efecto ni un render de más.
 */
export function useAsyncData<T>(
  cargar: (signal: AbortSignal) => Promise<T>,
  inicial: T,
): EstadoAsincrono<T> {
  // El valor inicial se congela en el primer render: si el llamante pasa un
  // literal (`[]`), su identidad cambia en cada render y no debe reiniciar nada.
  const inicialRef = useRef(inicial);
  const [intento, setIntento] = useState(0);
  const [resultado, setResultado] = useState<Resultado<T>>({
    intento: -1,
    datos: inicial,
    error: null,
  });

  useEffect(() => {
    const controlador = new AbortController();
    let vigente = true;

    cargar(controlador.signal)
      .then((datos) => {
        if (vigente) setResultado({ intento, datos, error: null });
      })
      .catch((error: unknown) => {
        if (vigente) setResultado({ intento, datos: inicialRef.current, error });
      });

    return () => {
      vigente = false;
      controlador.abort();
    };
  }, [cargar, intento]);

  const recargar = useCallback(() => {
    setIntento((anterior) => anterior + 1);
  }, []);

  return {
    datos: resultado.datos,
    cargando: resultado.intento !== intento,
    error: resultado.error,
    recargar,
  };
}
